import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, ExternalLink, Sparkles } from 'lucide-react'
import { mediaFileUrl } from 'components/Common/AppFloatingChat/chatLinks'

export type WebPageBlock = {
  type: 'web_page'
  title: string
  url: string
}

// ---------------------------------------------------------------------------
// Link check — create_web_page saves its pages in this app's media storage, at
// exports/<businessId>/<uuid>/<slug>.html. Saved messages and stream lines are not
// trusted, so the link is checked again before the page is shown or offered.
// ---------------------------------------------------------------------------

const PAGE_FILE = /^\/exports\/.+\.html$/i

/** The page's link when it is a page Coach made in this app's media storage, otherwise null. */
export const coachPageUrl = (url: unknown): string | null => {
  if (typeof url !== 'string') return null
  const page = mediaFileUrl(url)
  if (!page || page.search || page.hash) return null
  return PAGE_FILE.test(page.pathname) ? page.href : null
}

// A title is never near Lovable's 50,000-character prompt limit (a longer prompt makes Lovable
// drop the whole link), but a saved block is not trusted to be short.
const MAX_TITLE_LENGTH = 200

const titleOf = (title: unknown): string =>
  typeof title === 'string' ? Array.from(title.trim()).slice(0, MAX_TITLE_LENGTH).join('') : ''

/**
 * Lovable's "build with URL" link: a new Lovable project opens with this prompt (not sent yet)
 * and fetches the page as its reference. Its parameters go after the #, not in the query.
 */
export const lovableBuildUrl = (prompt: string, pageUrl: string): string =>
  `https://lovable.dev/#prompt=${encodeURIComponent(prompt)}&html=${encodeURIComponent(pageUrl)}`

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const COPIED_FOR_MS = 2000

const focusRing = 'focus:outline-none focus-visible:appShadow-focus'
// index.css gives every element its own text color, so icons and labels must follow their link
const inheritColor = '[color:inherit]'
const action = `inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-3 text-sm font-medium no-underline transition-colors ${focusRing}`

export const AppCoachWebPageBlock: React.FC<{ block: WebPageBlock }> = ({ block }) => {
  const { t } = useTranslation()
  const [copy, setCopy] = useState<'idle' | 'copied' | 'failed'>('idle')
  const url = coachPageUrl(block.url)
  const title = titleOf(block.title)
  const name = title || t('askCL.webPage.label')

  useEffect(() => {
    if (copy !== 'copied') return undefined
    const timer = window.setTimeout(() => setCopy('idle'), COPIED_FOR_MS)
    return () => window.clearTimeout(timer)
  }, [copy])

  if (!url) {
    return <p className="text-xs text-app-grey-65">{t('askCL.webPage.unavailable')}</p>
  }

  const copyLink = async () => {
    try {
      // Missing outside secure pages: that throws too, and ends up as the failure message
      await navigator.clipboard.writeText(url)
      setCopy('copied')
    } catch {
      setCopy('failed')
    }
  }

  return (
    <div className="[&_svg_*]:[color:inherit]">
      <p className="text-xs font-medium text-app-grey-65">{t('askCL.webPage.label')}</p>
      {title && (
        <h4 dir="auto" className="mt-0.5 break-words text-sm font-semibold text-app-grey-100">
          {title}
        </h4>
      )}
      {/* A small picture of the page as it looks on a computer: twice the box's size, shrunk by
          half. It never scrolls (the chat keeps the mouse wheel to itself) and takes no clicks:
          the link on top opens the page. The page runs no scripts in it (empty sandbox). */}
      <div
        dir="ltr"
        className="relative mt-2 h-48 w-full overflow-hidden rounded-lg border border-app-grey-25 bg-app-grey-5"
      >
        <iframe
          src={url}
          sandbox=""
          referrerPolicy="no-referrer"
          loading="lazy"
          scrolling="no"
          tabIndex={-1}
          title={t('askCL.webPage.previewTitle', { title: name })}
          className="pointer-events-none absolute left-0 top-0 h-[200%] w-[200%] origin-top-left scale-50 bg-white"
        />
        {/* Same link as "Open page" below, for the mouse only */}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={-1}
          aria-hidden="true"
          className="absolute inset-0"
        >
          <span className="sr-only">{t('askCL.webPage.open')}</span>
        </a>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${action} bg-app-primary-125 text-white hover:bg-app-primary-120`}
        >
          <ExternalLink aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
          {t('askCL.webPage.open')}
        </a>
        <button
          type="button"
          onClick={copyLink}
          className={`${action} border border-app-grey-40 bg-white text-app-grey-100 hover:bg-app-grey-10`}
        >
          {copy === 'copied' ? (
            <Check aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
          ) : (
            <Copy aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
          )}
          <span aria-live="polite" className={inheritColor}>
            {copy === 'copied' ? t('askCL.webPage.copied') : t('askCL.webPage.copyLink')}
          </span>
        </button>
        <a
          href={lovableBuildUrl(t('askCL.webPage.lovablePrompt', { title: name }), url)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${action} border border-app-primary-30 bg-app-primary-5 text-app-primary-125 hover:bg-app-primary-20`}
        >
          <Sparkles aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
          {t('askCL.webPage.buildWithLovable')}
        </a>
      </div>
      {copy === 'failed' && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.webPage.copyFailed')}
        </p>
      )}
      {/* The link is public: say so before it is shared */}
      <p className="mt-2 text-xs text-app-grey-65">{t('askCL.webPage.publicNotice')}</p>
      <p className="mt-1 text-xs text-app-grey-65">{t('askCL.webPage.lovableHint')}</p>
    </div>
  )
}

export default AppCoachWebPageBlock
